import React, { createContext, useContext, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AddIntegration from './components/AddIntegration';
import AlertConfig from './components/AlertConfig';
import Billing from './components/Billing';
import Sidebar from './components/Sidebar';
import StatusPage from './components/StatusPage';
import OnboardingTour from './components/OnboardingTour';
import './styles/App.css';

// ─── Auth Context ─────────────────────────────────────────────────────────────
const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

// ─── API Helper ───────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export async function apiCall(path, options = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}

// ─── Auth Provider ────────────────────────────────────────────────────────────
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
    return unsub;
  }, []);

  async function loginWithGoogle() { await signInWithPopup(auth, googleProvider); }
  async function logout() { await signOut(auth); }

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Protected Route ──────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (user) {
      const toured = localStorage.getItem(`ip-toured-${user.uid}`);
      if (!toured) {
        setTimeout(() => setShowTour(true), 800);
      }
    }
  }, [user]);

  function completeTour() {
    setShowTour(false);
    if (user) localStorage.setItem(`ip-toured-${user.uid}`, 'true');
  }

  if (loading) return (
    <div className="loading-screen">
      <div className="spinner" />
      <p style={{ fontSize: 13 }}>Loading IntegratePulse...</p>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="app-layout">
      <Sidebar onStartTour={() => setShowTour(true)} />
      <main className="main-content">
        {children}
      </main>
      {showTour && <OnboardingTour onComplete={completeTour} />}
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/status/:orgId" element={<StatusPage />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/integrations/add" element={<ProtectedRoute><AddIntegration /></ProtectedRoute>} />
              <Route path="/alerts/config" element={<ProtectedRoute><AlertConfig /></ProtectedRoute>} />
              <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
